import { useSwatches, useTypography } from "../hooks/makeswift-api";
import { colorToString } from "../../../components/utils/colorToString";
import { useResponsiveStyle } from "../../../components/utils/responsive-style";
import { findBreakpointOverride, shallowMergeFallbacks } from "../../../state/modules/breakpoints";
import { isNonNullable } from "../../../utils/isNonNullable";
import { useStyle } from "../use-style";
import { useBreakpoints } from "../hooks/use-breakpoints";
function typographyFragementToTypographyControlData(typography) {
  if (typography == null)
    return void 0;
  return {
    id: typography.id,
    style: typography.style.map(({ deviceId, value }) => ({
      deviceId,
      value: {
        fontFamily: value.fontFamily ?? void 0,
        lineHeight: value.lineHeight ?? void 0,
        letterSpacing: value.letterSpacing ?? void 0,
        fontWeight: value.fontWeight ?? void 0,
        textAlign: value.textAlign ?? void 0,
        uppercase: value.uppercase ?? void 0,
        underline: value.underline ?? void 0,
        strikethrough: value.strikethrough ?? void 0,
        italic: value.italic ?? void 0,
        fontSize: value.fontSize ?? void 0,
        color: value.color ?? void 0
      }
    }))
  };
}
function getTypographyStyleSwatchIds(style) {
  return style?.map((override) => override.value).flatMap((typographyStyle) => typographyStyle.color?.swatchId).filter(isNonNullable) ?? [];
}
const withColor = (swatches) => (deviceRawTypographyValue) => {
  const { value, deviceId } = deviceRawTypographyValue;
  if (value.color == null) {
    const { color, ...nextValue } = value;
    return {
      deviceId,
      value: nextValue
    };
  }
  return {
    deviceId,
    value: {
      ...value,
      color: {
        swatch: swatches.find((s) => s && s.id === value.color?.swatchId),
        alpha: value.color?.alpha ?? void 0
      }
    }
  };
};
const getDeviceId = ({ deviceId }) => deviceId;
function useEnhancedTypography(value) {
  const typography = typographyFragementToTypographyControlData(useTypography(value?.id ?? null));
  const source = typography?.style ?? [];
  const override = value?.style ?? [];
  const breakpoints = useBreakpoints();
  const swatchIds = [
    ...getTypographyStyleSwatchIds(value?.style),
    ...getTypographyStyleSwatchIds(typography?.style)
  ];
  const swatches = useSwatches(swatchIds).filter(isNonNullable);
  const enhancedSource = source.map(withColor(swatches));
  const enhancedOverride = override.map(withColor(swatches));
  const devices = [
    ...new Set(enhancedSource.map(getDeviceId).concat(enhancedOverride.map(getDeviceId)))
  ];
  return devices.map((deviceId) => {
    const deviceSource = findBreakpointOverride(breakpoints, enhancedSource, deviceId)?.value;
    const deviceOverride = findBreakpointOverride(
      breakpoints,
      enhancedOverride,
      deviceId,
      (v) => v
    )?.value;
    if (deviceSource && deviceOverride) {
      return {
        deviceId,
        value: { ...deviceSource, ...deviceOverride }
      };
    } else if (deviceOverride) {
      return {
        deviceId,
        value: deviceOverride
      };
    } else if (deviceSource) {
      return {
        deviceId,
        value: deviceSource
      };
    }
    return null;
  }).filter(isNonNullable);
}
function useTypographyClassName(value) {
  return useStyle(
    useResponsiveStyle(
      [value],
      ([value2]) => {
        if (value2 === void 0)
          return {};
        let styles = {};
        if (value2.color != null)
          styles.color = colorToString(value2.color);
        if (value2.fontFamily != null)
          styles.fontFamily = value2.fontFamily;
        if (value2.fontSize != null && value2.fontSize.value != null && value2.fontSize.unit != null)
          styles.fontSize = `${value2.fontSize.value}${value2.fontSize.unit}`;
        if (value2.fontWeight != null)
          styles.fontWeight = value2.fontWeight;
        if (value2.lineHeight != null)
          styles.lineHeight = value2.lineHeight;
        if (value2.letterSpacing != null)
          styles.letterSpacing = `${value2.letterSpacing / 10}em`;
        if (value2.uppercase != null)
          styles.textTransform = value2.uppercase === true ? "uppercase" : "initial";
        if (value2.underline != null || value2.strikethrough != null)
          styles.textDecoration = [
            Boolean(value2.underline) && "underline",
            Boolean(value2.strikethrough) && "line-through"
          ].filter(Boolean).join(" ");
        if (value2.italic != null)
          styles.fontStyle = value2.italic === true ? "italic" : "initial";
        return styles;
      },
      shallowMergeFallbacks
    )
  );
}
function useTypographyValue(data) {
  const enhancedTypography = useEnhancedTypography(data);
  return useTypographyClassName(enhancedTypography);
}
export {
  useEnhancedTypography as default,
  getTypographyStyleSwatchIds,
  typographyFragementToTypographyControlData,
  useTypographyClassName,
  useTypographyValue
};
//# sourceMappingURL=typography.js.map